import { GROUP_BY_COLUMN_DATA } from '../../shared/constants';
import { alignRightCellStyle, alignCenterCellStyle } from '@common/cellStyles';
import {
  legalEntityColumnDefinition,
  businessUnitColumnDefinition,
  countryNameColumnDefinition,
  countryTaxTypeColumnDefinition,
} from '../../../shared/columnDefinitions';
import { CellExportFormats } from '@common/xlsxExport/xlsxExport.constants';
import TaxExpenseTooltipRenderer from '../drillDetailLinkCellRenderer.component';
import { customNumeralValueFormatter } from '../../../shared/customNumeralValueFormatter';
import { NumeralTypes } from '@common/utils/formatNumberValue';
import { numeralValueFormatter } from '../../../shared/numeralValueFormatter';

export default ({
  groupByName,
  groupByField,
  groupItem,
  hasSourceCurrency,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
}) => [
  {
    headerName: groupByName,
    field: groupByField,
    minWidth: 120,
    pinned: 'left',
    ...alignCenterCellStyle,
  },
  ...(groupByName === GROUP_BY_COLUMN_DATA.CC.groupByName)
    ? [
      { ...legalEntityColumnDefinition, headerName: 'Legal Entity', field: 'LEGAL_ENTITY' },
      { ...businessUnitColumnDefinition, field: 'BUSINESS_UNIT' },
      { ...countryNameColumnDefinition, field: 'COUNTRY_CODE' },
    ]
    : [],
  ...(groupItem
    ? [{ ...countryTaxTypeColumnDefinition, headerName: 'Country Tax Type', field: 'COUNTRY_TAX_TYPE' }]
    : []
  ),
  {
    headerName: 'Currency',
    field: 'FUNC_CURRENCY',
    minWidth: 120,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'Status',
    field: 'STATUS_NAME',
    minWidth: 120,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'GAAP Income/(Loss) Before Tax',
    field: 'GAAP_IBT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Pre-Tax Adjustment',
    field: 'GAAP_IBT_ADJ',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Adjusted GAAP Income/(Loss) Before Tax',
    field: 'ADJ_GAAP_IBT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP to STAT Non-Reversing Differences',
    field: 'G2S_PERM',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP to STAT Reversing Differences',
    field: 'G2S_TEMP',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Statutory Income/(Loss) Before Tax',
    field: 'STAT_IBT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Permanent Differences',
    field: 'B2T_PERM',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Temporary Differences',
    field: 'B2T_TEMP',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Taxable Income/(Loss)',
    field: 'TXBL_INC',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Utilize Prior Year NOLs',
    field: 'UTILIZE_PY_NOL',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Taxable Income After NOL Utilization',
    field: 'TXBL_INC_AFT_NOL',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Record Current Year NOL',
    field: 'RECORD_CY_NOL',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Rate',
    field: 'TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Cur Tax Exp/(Benefit) Before Credits',
    field: 'GAAP_CUR_TAX_B4_CR',
    minWidth: 130,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Credits and Other After Tax Adj',
    field: 'TOTAL_CR',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Temporary Book to Tax Adjustments',
    field: 'B2T_TEMP_ADJ',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'G2S Deferred Tax (P)',
    field: 'G2S_DEF_TAX_P',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'G2S Deferred Tax (G)',
    field: 'GAAP_G2S_DEF_TAX_G',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Net Operating NOL',
    field: 'NO_NOL',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Record Valuation Allowance',
    field: 'RECORD_VA',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Deferreds',
    field: 'TOT_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Rate',
    field: 'DEF_TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Expense',
    field: 'GAAP_DEF_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Cur Tax Exp - Funct Currency',
    field: 'GAAP_CUR_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Def Tax Exp - Funct Currency',
    field: 'TOT_GAAP_GAAP_DEF_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total After Tax Deferred Tax Expense',
    field: 'GAAP_DEF_PR_CR_UTL',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Exp on CY Activity - Funct Currency',
    field: 'GAAP_TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'CY Effective Tax Rate',
    field: 'GAAP_CY_EFF_TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Currency',
    field: 'TAX_CURRENCY',
    minWidth: 120,
    hide: !hasSourceCurrency,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'MOR',
    field: 'TAX_CUR_MOR',
    minWidth: 120,
    hide: !hasSourceCurrency,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Current Tax Expense - Tax Currency',
    field: 'CUR_TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Deferred Tax Expense - Tax Currency',
    field: 'GAAP_DEF_TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Expense on CY Activity - Tax Currency',
    field: 'GAAP_TAX_EXP_CY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Current)',
    field: 'GAAP_RES_CUR_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Non Current)',
    field: 'GAAP_RES_NON_CUR_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Penalty)',
    field: 'GAAP_RES_PEN_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Interest)',
    field: 'GAAP_RES_INT_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Other Provision (Current)',
    field: 'GAAP_OTH_PROV_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Total Other - CY Activity (Current) - Tax Currency',
    field: 'GAAP_TOT_CUR_AFT_TAX_CY',
    minWidth: 130,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Other Provision (Deferred)',
    field: 'GAAP_OTH_PROV_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Total Other - CY Activity (Deferred) - Tax Currency',
    field: 'GAAP_TOT_DEF_AFT_TAX_CY',
    minWidth: 130,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Total Current Tax Expense on CY Activity - Tax Currency',
    field: 'GAAP_TOT_CUR_TAX_EXP_CY',
    minWidth: 140,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Total Deferred Tax Expense on CY Activity - Tax Currency',
    field: 'GAAP_TOT_DEF_TAX_EXP_CY',
    minWidth: 150,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Total Tax Expense on CY Activity - Tax Currency',
    field: 'GAAP_TOT_TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Return to Accrual (Current)',
    field: 'GAAP_RTA_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Post Return Adjustments (Current)',
    field: 'GAAP_PRA_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'True Up (Payable)',
    field: 'GAAP_TRUE_UP_PAY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Current)',
    field: 'GAAP_RES_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Non Current)',
    field: 'GAAP_RES_NON_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Interest)',
    field: 'GAAP_RES_INT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Penalty)',
    field: 'GAAP_RES_PEN',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Total Current Tax Expense - PY Activity - Tax Currency',
    field: 'GAAP_TOT_CUR_AFT_TAX_PY',
    minWidth: 140,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Return to Accrual (Deferred)',
    field: 'GAAP_RTA_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },

  {
    headerName: 'Post Return Adjustments (Deferred)',
    field: 'GAAP_PRA_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },

  {
    headerName: 'True Up (Deferred)',
    field: 'GAAP_TRUE_UP_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Rate Changes (Deferred)',
    field: 'GAAP_RATE_CHG_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Total Deferred Tax Expense - PY Activity - Tax Currency',
    field: 'GAAP_TOT_DEF_AFT_TAX_PY',
    minWidth: 150,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Total Tax Expense on PY Activity - Tax Currency',
    field: 'GAAP_TAX_EXP_PY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Current Tax Expense - Total Activity',
    field: 'GAAP_CUR_TAX_EXP_TOT_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Deferred Tax Expense - Total Activity',
    field: 'GAAP_DEF_TAX_EXP_TOT_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Tax Expense',
    field: 'GAAP_TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: hasSourceCurrency
      ? 'Adjusted GAAP Income/(Loss) Before Tax (Tax Currency)'
      : 'Adjusted GAAP Income/(Loss) Before Tax',
    field: 'GAAP_IBT_TAX_CUR',
    minWidth: 130,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'GAAP Effective Tax Rate',
    field: 'GAAP_ETR',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
];
